/* eslint-disable indent */
import React from 'react';
import { useNavigate } from 'react-router';
import { PagePath } from '../../../../../navigation/navigation.enums';
import { PageLoaderLayer } from '../../../../../components/general/loading-state/loader-layers/page-loader-layer/page-loader-layer';
import { Button, PageHeader, UnorderedList, UnorderedListItem } from '@keplerco/core';
import { useAppState } from '../../../../../overmind';
import { CompletionStatus } from '../../../../../enums';
import classNames from 'classnames';
import { RateYourPeersSvg } from './rate-your-peers.svg';
import { useAnalysisBasePagePath } from '../../../use-analysis-base-page-path';
import analysisStyles from '../../../analysis.module.css';

export function RateYourPeersSkillsPage(): JSX.Element {
  const navigate = useNavigate();
  const { activeAssessmentConfig, forcedAssessments } = useAppState();
  const basePagePath = useAnalysisBasePagePath();

  function breadcrumbConfiguration() {
    const breadcrumbs = forcedAssessments.length > 1
      ? [
          {
            name: 'Pending assessments',
            url: PagePath.analysisBase,
          },
          {
            name: activeAssessmentConfig?.assessmentName ?? 'Assessment',
            url: basePagePath,
          },
        ]
      : [
          {
            name: activeAssessmentConfig?.assessmentName ?? 'Assessment',
            url: basePagePath,
          },
        ];

    if (activeAssessmentConfig?.selfReviewCompletionStatus !== CompletionStatus.NoActionRequired) {
      breadcrumbs.push(
        {
          name: 'Frequency of use',
          url: basePagePath + PagePath.analysisFrequencyOfUse,
        },
        {
          name: 'Self assessment',
          url: basePagePath + PagePath.analysisSelfAssessment,
        },
        {
          name: `Rate your peers' skills`,
          url: basePagePath + PagePath.analysisSelfAssessment + PagePath.analysisRateYourPeersSkills,
        }
      );
    } else {
      breadcrumbs.push({
        name: `Rate your peers' skills`,
        url: basePagePath + PagePath.analysisRateYourPeersSkills,
      });
    }

    return breadcrumbs;
  }

  function handlePeerRatingNavigation() {
    if (activeAssessmentConfig?.selfReviewCompletionStatus !== CompletionStatus.NoActionRequired) {
      navigate(`${basePagePath}${PagePath.analysisSelfAssessment + PagePath.analysisPeerSkillsAssessment}`);
    } else {
      navigate(`${basePagePath}${PagePath.analysisPeerSkillsAssessment}`);
    }
  }
  return (
    <PageLoaderLayer path={PagePath.analysisRateYourPeersSkills}>
      <div className={classNames('pageWrapper wrapper', analysisStyles.interstitialPage)}>
        <PageHeader breadcrumbs={breadcrumbConfiguration()} />

        <section className={analysisStyles.interstitialSection}>
          <figure className={analysisStyles.interstitialFigure}>
            <div className={analysisStyles.interstitialSVG}>
              <RateYourPeersSvg />
            </div>
          </figure>

          <div className={analysisStyles.interstitialContent}>
            <h1>Rate your peers' skills</h1>
            <p style={{ marginTop: 4, color: 'var(--text_1)' }}>Help your team grow by sharing your honest feedback.</p>

            <div style={{ marginTop: 24, marginBottom: 24 }}>
              <UnorderedList gap="12px">
                <UnorderedListItem bulletTop="0.4rem">
                  <p style={{ color: 'var(--text_1)' }}>Select the colleagues you'd like to assess. No need to rate everyone if you are unable to.</p>
                </UnorderedListItem>

                <UnorderedListItem bulletTop="0.4rem">
                  <p style={{ color: 'var(--text_1)' }}>Provide fair, constructive, and specific feedback for each skill.</p>
                </UnorderedListItem>

                <UnorderedListItem bulletTop="0.4rem">
                  <p style={{ color: 'var(--text_1)' }}>Your input helps the team and individuals improve and grow.</p>
                </UnorderedListItem>
              </UnorderedList>
            </div>

            <Button type="button" onClick={handlePeerRatingNavigation}>
              Start assessment
            </Button>
          </div>
        </section>
      </div>
    </PageLoaderLayer>
  );
}
